// @ts-nocheck
// GENERATED from PropelMapper/design@b27d324 — do not edit.
import * as React from "react";
// PMField.tsx — PropelMapper standardized form field.
//
// One control for text / textarea / select / date / time / email / number,
// with a consistent label, optional leading icon, and — the part app screens
// keep re-inventing — a built-in HELPER / INFORMATION line and an ERROR line.
//
// Helper text (grey, --pm-fg-3) is always-visible guidance: units, format
// hints, "time optional", privacy notes. Error text (red, --pm-error) replaces
// it when present and reddens the border. Pass `optional` to show an "Optional"
// indicator instead of a required asterisk.
//
// Tokens only (CSS vars), so it themes with the rest of the system.

function PMInfoGlyph({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }} aria-hidden="true">
      <circle cx="8" cy="8" r="6.4" stroke="currentColor" strokeWidth="1.3" />
      <circle cx="8" cy="5.2" r="0.95" fill="currentColor" />
      <path d="M8 7.3v4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function PMErrorGlyph({ size = 13 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }} aria-hidden="true">
      <circle cx="8" cy="8" r="6.4" stroke="currentColor" strokeWidth="1.3" />
      <path d="M8 4.6v4.2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <circle cx="8" cy="11.2" r="0.95" fill="currentColor" />
    </svg>
  );
}

function PMChevronGlyph({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }} aria-hidden="true">
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function PMField({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  optional = false,
  placeholder,
  helpText,
  error,
  options = [],
  rows = 3,
  maxLength,
  showCount = false,
  leadingIcon = null,
  disabled = false,
  id,
  name,
  autoFocus = false,
  style = {},
  inputStyle = {},
  ...rest
}: any) {
  const [focused, setFocused] = (React as any).useState(false);
  const hasError = !!error;
  const isArea = type === "textarea";
  const isSelect = type === "select";

  const borderColor = hasError
    ? "var(--pm-error)"
    : focused
    ? "var(--pm-primary)"
    : "var(--pm-border)";

  const controlBase: any = {
    width: "100%",
    boxSizing: "border-box",
    border: "none",
    outline: "none",
    background: "transparent",
    font: "400 14px/1.5 var(--pm-font-sans)",
    color: disabled ? "var(--pm-fg-3)" : "var(--pm-fg-1)",
    padding: isArea ? "12px 0" : "13px 0",
    minWidth: 0,
    resize: "none",
    appearance: isSelect ? "none" : undefined,
    WebkitAppearance: isSelect ? "none" : undefined,
    cursor: disabled ? "not-allowed" : isSelect ? "pointer" : "text",
    ...inputStyle,
  };

  const commonProps: any = {
    id,
    name,
    value,
    onChange,
    disabled,
    placeholder,
    autoFocus,
    onFocus: () => setFocused(true),
    onBlur: () => setFocused(false),
    style: controlBase,
    ...rest,
  };

  let control: any;
  if (isArea) {
    control = (React as any).createElement("textarea", { ...commonProps, rows, maxLength });
  } else if (isSelect) {
    control = (React as any).createElement(
      "select",
      commonProps,
      (options || []).map((o: any) => {
        const v = typeof o === "object" ? o.value : o;
        const l = typeof o === "object" ? o.label : o;
        return (React as any).createElement("option", { key: String(v), value: v }, l);
      })
    );
  } else {
    control = (React as any).createElement("input", { ...commonProps, type, maxLength });
  }

  const charCount =
    showCount && maxLength != null
      ? `${(value ? String(value).length : 0)} / ${maxLength}`
      : null;

  return (
    <div style={{ fontFamily: "var(--pm-font-sans)", ...style }}>
      {label && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            font: "600 12px/1.4 var(--pm-font-sans)",
            color: "var(--pm-fg-2)",
            marginBottom: 6,
          }}
        >
          <span>{label}</span>
          {required && <span style={{ color: "var(--pm-error)", fontWeight: 700 }}>*</span>}
          {optional && !required && (
            <span style={{ marginLeft: 2, font: "500 11px/1 var(--pm-font-sans)", color: "var(--pm-fg-3)" }}>
              Optional
            </span>
          )}
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: isArea ? "flex-start" : "center",
          gap: 10,
          border: `1px solid ${borderColor}`,
          borderRadius: "var(--pm-radius-l)",
          padding: "0 14px",
          background: disabled ? "var(--pm-surface-variant)" : "var(--pm-surface)",
          transition: "border-color 150ms ease, box-shadow 150ms ease",
          boxShadow: focused && !hasError ? "0 0 0 3px color-mix(in srgb, var(--pm-primary) 14%, transparent)" : "none",
        }}
      >
        {leadingIcon && (
          <span
            style={{
              flexShrink: 0,
              display: "inline-flex",
              marginTop: isArea ? 13 : 0,
              color: focused ? "var(--pm-primary)" : "var(--pm-fg-3)",
            }}
          >
            {leadingIcon}
          </span>
        )}
        {control}
        {isSelect && (
          <span style={{ flexShrink: 0, display: "inline-flex", color: "var(--pm-fg-3)", pointerEvents: "none" }}>
            <PMChevronGlyph />
          </span>
        )}
      </div>

      {(error || helpText || charCount) && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 6,
            minHeight: 16,
          }}
        >
          {(error || helpText) && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                flex: 1,
                font: "400 12px/1.4 var(--pm-font-sans)",
                color: hasError ? "var(--pm-error)" : "var(--pm-fg-3)",
              }}
            >
              {hasError ? <PMErrorGlyph /> : <PMInfoGlyph />}
              <span style={{ textWrap: "pretty" } as any}>{error || helpText}</span>
            </div>
          )}
          {charCount && (
            <span
              style={{
                marginLeft: "auto",
                flexShrink: 0,
                font: "500 11px/1.4 var(--pm-font-sans)",
                color: "var(--pm-fg-3)",
              }}
            >
              {charCount}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
